import { forwardRef } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

const DEFAULT_VARIANT = 'fill' as const

const badgeVariants = tv(
  {
    base: 'font-body inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-[4px] border-[0.7px] px-1 text-center font-semibold',
    variants: {
      variant: {
        primary: 'bg-primary border-primary text-white',
        warning: 'bg-warning-bg border-warning-stroke text-warning-title',
        danger: 'bg-danger-bg border-danger-stroke text-danger-title',
        success: 'bg-success-bg border-success-stroke text-success-title',
        fill: 'bg-information-bg border-information-stroke text-information-title',
        purple: 'bg-purple-bg border-purple-stroke text-purple-title',
        blue: 'bg-blue-bg border-blue-stroke text-blue-title',
        teal: 'bg-teal-bg border-teal-stroke text-teal-title',
        brown: 'bg-brown-bg border-brown-stroke text-brown-title',
        open: 'bg-light-gray-green border-light-gray-green text-white',
        canceled: 'bg-surface border-surface-300 text-surface-body',
        info: 'bg-pacific-blue border-pacific-blue text-white',
        outline:
          'bg-surface-subtle border-border-neutral text-surface-title-50',
        'danger-light':
          'bg-danger-light-bg border-danger-light-stroke text-danger-light-title',
        'danger-soft':
          'bg-light-orange border-light-orange text-danger-soft-title'
      },
      size: {
        md: 'text-caption-lg h-[26px] w-[104px]',
        sm: 'text-caption-md h-[19px] w-[88px]'
      }
    },
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      size: 'md'
    }
  },
  /* tv's bundled tailwind-merge doesn't know the Ømni text tokens, so it
     classifies `text-caption-*` as a text color and drops the variant's
     real color class. Merging is owned by the cn() below instead, whose
     registry maps the Ømni tokens to the font-size group. */
  { twMerge: false }
)

export type BadgeVariant = NonNullable<
  VariantProps<typeof badgeVariants>['variant']
>

export type BadgeProps = React.ComponentPropsWithRef<'span'> &
  VariantProps<typeof badgeVariants>

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant, size, className, ...props }, ref) => (
    <span
      ref={ref}
      data-slot='badge'
      data-variant={variant || DEFAULT_VARIANT}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
)
Badge.displayName = 'Badge'
